import { useEffect, useState } from "react"
import { ScrollText } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams } from "react-router-dom"

import { BreadcrumbNav } from "@/components/breadcrumb-nav"
import { EmptyState } from "@/components/empty-state"
import { LoadingState } from "@/components/loading-state"
import { Button } from "@/components/ui/button"
import { BordereauCard } from "@/features/intake/components/bordereau-card"
import { DecisionCard } from "@/features/intake/components/decision-card"
import { DecisionPlaceholder } from "@/features/intake/components/decision-placeholder"
import { LabCard } from "@/features/intake/components/lab-card"
import { LoadStepper } from "@/features/intake/components/load-stepper"
import { LoadSummaryHeader } from "@/features/intake/components/load-summary-header"
import { QualitySummaryCard } from "@/features/intake/components/quality-summary-card"
import { RefusedCard } from "@/features/intake/components/refused-card"
import { SamplingCard } from "@/features/intake/components/sampling-card"
import { ScaleCard } from "@/features/intake/components/scale-card"
import { useLoad } from "@/features/intake/hooks/use-intake"
import type { LoadStatus } from "@/features/intake/api/types"
import { paths } from "@/routes/paths"

export function IntakeDetailPage() {
  const { t } = useTranslation(["intake", "common"])
  const navigate = useNavigate()
  const { loadId = "" } = useParams<{ loadId: string }>()
  const { data: load, isLoading } = useLoad(loadId)

  const samplingDone = !!load?.sampling
  const labDone = !!load?.labResults
  const qualityDone = samplingDone && labDone

  const [editingSampling, setEditingSampling] = useState(false)
  const [editingLab, setEditingLab] = useState(false)

  // Local "viewed" stage for stepper navigation. Defaults to whatever
  // the server says is current; users can jump back to inspect prior
  // stages without changing the load's real status.
  const [viewedStage, setViewedStage] = useState<LoadStatus | null>(null)
  const effectiveStage: LoadStatus = viewedStage ?? load?.status ?? "SCALE"

  // Reset the viewed stage when the underlying load changes (e.g.
  // navigation between loads) so we don't carry over a stale view.
  useEffect(() => {
    setViewedStage(null)
  }, [load?.id])

  // If the persisted data disappears (edge case: user re-opens before refetch),
  // drop the editing flag so we don't strand the user in a non-cancelable form.
  useEffect(() => {
    if (!samplingDone) setEditingSampling(false)
  }, [samplingDone])
  useEffect(() => {
    if (!labDone) setEditingLab(false)
  }, [labDone])

  // Lock editing when the load is fully completed (nothing should be
  // editable any more) OR when the user is inspecting a stage that's
  // not the current one. Computed up here (before the early returns)
  // so the dependent effect below stays in the same hook position
  // across renders.
  const isCompleted = load?.status === "COMPLETED"
  const isViewingPast = !!load && effectiveStage !== load.status
  const lockEditing = isCompleted || isViewingPast

  // Force-close any active editing whenever we lock down — so jumping
  // away from QUALITY or hitting COMPLETED kicks the user out of edit
  // mode cleanly.
  useEffect(() => {
    if (lockEditing) {
      setEditingSampling(false)
      setEditingLab(false)
    }
  }, [lockEditing])

  if (isLoading) {
    return <LoadingState />
  }

  if (!load) {
    return (
      <EmptyState
        title={t("errors.loadNotFound", { ns: "intake" })}
        action={
          <button
            type="button"
            onClick={() => navigate(paths.reception)}
            className="text-sm text-primary underline-offset-4 hover:underline"
          >
            {t("detail.back", { ns: "intake" })}
          </button>
        }
      />
    )
  }

  const isEditingQuality = editingSampling || editingLab
  const showDecision = qualityDone && !isEditingQuality

  return (
    <div className="flex flex-col gap-6">
      <BreadcrumbNav
        crumbs={[
          { label: t("title", { ns: "intake" }), to: paths.reception },
          {
            label: load.loadNumber
              ? t("detail.loadHeading", {
                  ns: "intake",
                  number: load.loadNumber,
                })
              : t("detail.noNumber", { ns: "intake" }),
          },
        ]}
      />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <LoadSummaryHeader load={load} />
        {load.deliveryFormId ? (
          <Button asChild variant="outline" size="sm">
            <Link to={paths.deliveryFormDetail(load.deliveryFormId)}>
              <ScrollText className="size-4" />
              {t("waveIn.viewTrace", { ns: "intake" })}
            </Link>
          </Button>
        ) : null}
      </div>
      <LoadStepper
        status={load.status}
        decision={load.decision}
        viewedStage={effectiveStage}
        onJump={(s) => setViewedStage(s)}
      />

      <div className="flex flex-col gap-4">
        {effectiveStage === "SCALE" ? (
          <ScaleCard load={load} readOnly={lockEditing} />
        ) : null}

        {effectiveStage === "QUALITY" ? (
          isCompleted ? (
            <QualitySummaryCard load={load} />
          ) : (
            <>
              <SamplingCard
                load={load}
                isEditing={editingSampling}
                onEditingChange={setEditingSampling}
                readOnly={lockEditing}
              />
              <LabCard
                load={load}
                isEditing={editingLab}
                onEditingChange={setEditingLab}
                readOnly={lockEditing}
              />
              {showDecision ? (
                <DecisionCard load={load} readOnly={lockEditing} />
              ) : (
                <DecisionPlaceholder />
              )}
            </>
          )
        ) : null}

        {effectiveStage === "COMPLETED" ? (
          <>
            <QualitySummaryCard load={load} />
            {load.decision === "ACCEPTED" ? (
              <BordereauCard load={load} />
            ) : (
              <RefusedCard load={load} />
            )}
          </>
        ) : null}
      </div>
    </div>
  )
}
